import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { fetchAuthSession } from 'aws-amplify/auth';
import { graphqlMutation, graphqlQuery } from '../utils/graphqlApi';
import { classify as classifyMutation, createIncident, updateIncident, refreshImageUrls as refreshImageUrlsMutation, requestRunbook as requestRunbookMutation, triggerDevopsAgent, triggerDevopsInvestigation, submitDevopsAgentFeedback, invokeAgent } from '../graphql/mutations';
import { checkDevopsStatus, getSessionTimeline, getRunbook } from '../graphql/queries';
import { config } from '../config/appConfig';
import { getApprovalMessage, AWAITING_APPROVAL_STATUSES } from '../utils/sessionHelpers';
import useUserPermissions from '../hooks/useUserPermissions';

const INCIDENT_TTL_SECONDS = 7 * 24 * 60 * 60;

const parseScenarioHeader = (description = '') => {
  if (typeof description !== 'string') return null;
  const normalized = description
    .replace(/^\s*[-*]\s*/, '')
    .replace(/^\s*\*\*/, '')
    .replace(/^[\s"'“”‘’`]+/, '')
    .trim();

  const lines = normalized
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  // Accept common formats in first meaningful line:
  // - Scenario 1
  // - Scenario 1:
  // - Scenario 1: Title
  // - Scenario 1 - Title
  const primaryLine = lines[0] || normalized;
  let match = primaryLine.match(/^scenario\s*(\d+)\s*(?:[:\-–—]\s*)?(.*)$/i);

  // Fallback: scenario appears later in the line (e.g. prefixed text/emoji)
  if (!match) {
    match = primaryLine.match(/\bscenario\s*(\d+)\b\s*(?:[:\-–—]\s*)?(.*)$/i);
  }

  if (!match) return null;

  const rawTitle = (match[2] || '')
    .replace(/^[\s:;\-–—]+/, '')
    .replace(/\*+$/, '')
    .trim();
  return {
    number: match[1],
    title: rawTitle,
  };
};

function IncidentView() {
  const navigate = useNavigate();
  const location = useLocation();
  const { canInvokeServiceNeedAgent, canInvokeCEPAgent } = useUserPermissions();
  const [incidentNumber, setIncidentNumber] = useState('');
  const [incidentDescription, setIncidentDescription] = useState('');
  const [description, setDescription] = useState('');
  const [tribeInformation, setTribeInformation] = useState('');
  const [classificationResult, setClassificationResult] = useState(null);
  // const [isProceedEnabled, setIsProceedEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // --- New state for updated classification flow ---
  const [dynamodbResults, setDynamodbResults] = useState([]);
  const [activeView, setActiveView] = useState(null); // 'runbook' | 'aiAgent' | 'requestRunbook'
  const [stepFeedback, setStepFeedback] = useState({}); // { stepIndex: { rating: 'up'|'down', reason: '' } }
  const [overallFeedback, setOverallFeedback] = useState({ rating: null, reason: '' }); // overall
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [aiAgentStatus, setAiAgentStatus] = useState(null); // null | 'working' | 'done' | 'error'
  const [currentAgentStep, setCurrentAgentStep] = useState(null); // latest step from step-poll
  const pollingRef = useRef(null);
  const seenIntermediateStep = useRef(false); // track if we've seen a non-final step
  const pollErrorCount = useRef(0); // track consecutive poll errors
  const [requestNotified, setRequestNotified] = useState(false);
  const [hasAutomation, setHasAutomation] = useState(false);
  const [isRequestingRunbook, setIsRequestingRunbook] = useState(false);
  const [incidentDatetime, setIncidentDatetime] = useState(null); // Stored after createIncident for updateIncident


  // --- DevOps Investigate state ---
  const [investigateStatus, setInvestigateStatus] = useState(null); // null | 'working' | 'done' | 'error'
  const [investigateMessage, setInvestigateMessage] = useState('');
  const [investigateReport, setInvestigateReport] = useState(''); // markdown content
  const [copiedBlock, setCopiedBlock] = useState(null); // track which code block was copied
  const investigatePollRef = useRef(null);
  const [detectedAlarmName, setDetectedAlarmName] = useState(null); // alarm extracted from short description
  const [alarmMode, setAlarmMode] = useState(false); // true = alarm detected, skip classification
  const [serviceNeedMode, setServiceNeedMode] = useState(false); // true = service need (SN) detected, skip classification
  const [serviceNeedAgentStatus, setServiceNeedAgentStatus] = useState(null); // null | 'invoking' | 'polling' | 'done' | 'error'
  const [serviceNeedAgentMessage, setServiceNeedAgentMessage] = useState('');
  const [serviceNeedAgentFindings, setServiceNeedAgentFindings] = useState([]); // accumulated timeline steps for display on timeout/error
  const [serviceNeedCodeCheckReason, setServiceNeedCodeCheckReason] = useState(''); // INVOKE_CODE_CHECKER reason from DECISION entry

  const serviceNeedPollRef = useRef(null);
  const [investigateSessionId, setInvestigateSessionId] = useState(null); // session id from triggerDevopsAgent; reused by the Plan step
  const [investigateS3Path, setInvestigateS3Path] = useState(null); // S3 path of the devops agent report
  // eslint-disable-next-line no-unused-vars
  const [hasPlanEntry, setHasPlanEntry] = useState(false); // true when session has >=1 PLAN entry
  // eslint-disable-next-line no-unused-vars
  const [planSessionId, setPlanSessionId] = useState(null); // session id that owns the PLAN entry (may differ from investigateSessionId)
  const [toastMessage, setToastMessage] = useState(null); // transient toast (e.g. plan rejected)
  const [devopsFeedback, setDevopsFeedback] = useState({ rating: null, comment: '' });
  const [devopsFeedbackSubmitted, setDevopsFeedbackSubmitted] = useState(false);
  const [devopsFeedbackError, setDevopsFeedbackError] = useState(null);
  const [isSubmittingDevopsFeedback, setIsSubmittingDevopsFeedback] = useState(false);
  const [environmentType, setEnvironmentType] = useState(null); // null | 'AWS' | 'Non-AWS'
  const [selectedRunbookScenarioKey, setSelectedRunbookScenarioKey] = useState('');
  const [showAllRunbookScenarios, setShowAllRunbookScenarios] = useState(false);

  // --- Plan Agent state (View Plan → invoke orchestrator → poll timeline for PLAN) ---
  const [planAgentStatus, setPlanAgentStatus] = useState(null); // null | 'invoking' | 'polling' | 'ready' | 'error'
  const [planAgentError, setPlanAgentError] = useState('');
  const planAgentPollRef = useRef(null);
  // Holds the sessionId pre-generated at Investigate time so handleViewPlan can
  // reuse it (and the plan may already be ready by the time the user clicks View Plan).
  const earlyPlanSessionIdRef = useRef(null);

  // Refs to mirror feedback state so buildFeedbackPayload always reads current values
  const stepFeedbackRef = useRef(stepFeedback);
  const overallFeedbackRef = useRef(overallFeedback);
  const dynamodbResultsRef = useRef(dynamodbResults);
  const incidentNumberRef = useRef(incidentNumber);
  const incidentDatetimeRef = useRef(incidentDatetime);
  const userEmailRef = useRef('');

  // Keep refs in sync with state
  useEffect(() => { stepFeedbackRef.current = stepFeedback; }, [stepFeedback]);
  useEffect(() => { overallFeedbackRef.current = overallFeedback; }, [overallFeedback]);
  useEffect(() => { dynamodbResultsRef.current = dynamodbResults; }, [dynamodbResults]);
  useEffect(() => { incidentNumberRef.current = incidentNumber; }, [incidentNumber]);
  useEffect(() => { incidentDatetimeRef.current = incidentDatetime; }, [incidentDatetime]);

  // Fetch and cache the current user's email on mount
  useEffect(() => {
    const loadUserEmail = async () => {
      try {
        const { tokens } = await fetchAuthSession();
        const idToken = tokens?.idToken;
        if (idToken) {
          const payload = JSON.parse(atob(idToken.toString().split('.')[1]));
          userEmailRef.current = payload.email || payload.preferred_username || payload['cognito:username'] || '';
        }
      } catch (err) {
        console.warn('[IncidentView] Could not retrieve user email:', err);
      }
    };
    loadUserEmail();
  }, []);

  const getStepNumber = (step, index) => step.step_number || step['Step Number'] || String(index + 1);
  const getStepDescription = (step) => step.description || step.step_description || step.Description || '';
  const getStepImageUrl = (step) => step.image_url || step.imageUrl || step.URL || step.url || '';
  const getStepImageKey = (step) => step.image_s3_key || step.imageS3Key || step.s3_key || step.s3Key || '';

  const runbookScenarioSections = useMemo(() => {
    const sections = [];
    let currentSection = {
      key: 'general',
      label: 'General',
      scenarioNumber: null,
      entries: [],
    };

    dynamodbResults.forEach((step, index) => {
      const description = getStepDescription(step);
      const parsedScenario = parseScenarioHeader(description);

      if (parsedScenario) {
        if (currentSection.entries.length > 0) {
          sections.push(currentSection);
        }

        currentSection = {
          key: `scenario-${parsedScenario.number}-${parsedScenario.title || index}`,
          label: `Scenario ${parsedScenario.number}${parsedScenario.title ? `: ${parsedScenario.title}` : ''}`,
          scenarioNumber: parsedScenario.number,
          entries: [{ step, index }],
        };
      } else {
        currentSection.entries.push({ step, index });
      }
    });

    if (currentSection.entries.length > 0) {
      sections.push(currentSection);
    }

    return sections;
  }, [dynamodbResults]);

  const runbookScenarioCount = runbookScenarioSections.filter((section) => section.scenarioNumber).length;
  const runbookHasScenarioLayout = runbookScenarioCount > 1;

  useEffect(() => {
    if (!runbookScenarioSections.length) {
      setSelectedRunbookScenarioKey('');
      return;
    }

    if (
      !selectedRunbookScenarioKey
      || !runbookScenarioSections.some((section) => section.key === selectedRunbookScenarioKey)
    ) {
      const defaultSection = runbookScenarioSections.find((section) => section.scenarioNumber) || runbookScenarioSections[0];
      setSelectedRunbookScenarioKey(defaultSection.key);
    }
  }, [runbookScenarioSections, selectedRunbookScenarioKey]);

  const visibleRunbookSections = !runbookHasScenarioLayout
    ? runbookScenarioSections
    : showAllRunbookScenarios
      ? runbookScenarioSections
      : runbookScenarioSections.filter((section) => section.key === selectedRunbookScenarioKey);

  // --- Extract alarm name from short description ---
  const extractAlarmName = (shortDesc) => {
    if (!shortDesc || typeof shortDesc !== 'string') return null;

    // Alarm name character class — alphanumeric plus ._/-:
    const ALARM_CHAR = '[A-Za-z0-9._/:-]';
    const ALARM_NAME = `${ALARM_CHAR}+`;

    // --- Ordered patterns (most specific first) ---
    const alarmPatterns = [
      // "Alarm name: alarmName" or "Alarm name:alarmName"
      new RegExp(`Alarm\\s+name\\s*:\\s*(?:[""]+)?\\s*(${ALARM_NAME})`, 'i'),
      // "Amazon CloudWatch alarm "name"" or 'Amazon CloudWatch alarm "name"'
      new RegExp(`Amazon\\s+CloudWatch\\s+alarm\\s+["""]+(${ALARM_NAME})`, 'i'),
      // ALARM:- "name" or ALARM:- ""name""
      new RegExp(`AL(?:A|LA)RM\\s*:?-?\\s*["""]+(${ALARM_NAME})`, 'i'),
      // ALARM:"name" / ALARM: "name" / ALARM : "name" / ALARM: ""name""
      new RegExp(`AL(?:A|LA)RM\\s*:?\\s*["""]+(${ALARM_NAME})`, 'i'),
      // ALARM"" name (no colon, smart-quotes)
      new RegExp(`ALARM\\s*[""]+\\s*(${ALARM_NAME})`, 'i'),
      // ALARM: name / ALARM : name / Alarm: name (no quotes at all)
      new RegExp(`AL(?:A|LA)RM\\s*:?\\s+(${ALARM_NAME})`, 'i'),
      // Alarm inside parentheses: "...some text (alarm-name)"
      new RegExp(`\\((${ALARM_NAME}(?:-${ALARM_NAME})*)\\)\\s*$`, 'i'),
    ];

    for (const pattern of alarmPatterns) {
      const match = shortDesc.match(pattern);
      if (match && match[1]) {
        let name = match[1].trim();
        // Strip trailing quotes/punctuation
        name = name.replace(/[""".,:;!]+$/, '');
        // Alarm names should be reasonably long and contain a hyphen or slash
        if (name.length > 5 && (name.includes('-') || name.includes('/') || name.includes('_'))) {
          return name;
        }
      }
    }

    // --- Entire short description is a bare alarm name ---
    const trimmed = shortDesc.trim();
    if (
      /^[A-Za-z0-9][A-Za-z0-9._/:-]+$/.test(trimmed) &&
      (trimmed.includes('-') || trimmed.includes('/') || trimmed.includes('_')) &&
      trimmed.length > 10 &&
      !/\s/.test(trimmed)
    ) {
      return trimmed;
    }

    return null;
  };

  // Detect all Service Need tickets by token patterns like "SN003120".
// Matching is case-insensitive (the /i flag) so lowercase variants such as
// "sn003120" are also detected, even though the canonical ticket format is uppercase SN.
  const extractServiceNeeds = (text) => {
    if (!text || typeof text !== 'string') return [];
    return text.match(/\bSN\d+\b/gi) || [];
  };

  // Strip URLs/hyperlinks from agent output text (Service Need tab should show plain text only)
  const stripUrls = (text) => {
    if (!text || typeof text !== 'string') return text;
    return text
      .replace(/\[([^\]]*)\]\([^)]+\)/g, '')     // [label](url) → remove entirely
      .replace(/https?:\/\/[^\s)>\]]+/g, '')     // bare URLs → remove entirely
      .replace(/\s{2,}/g, ' ')                   // collapse extra spaces
      .trim();
  };

  const handleClassify = async () => {
    if (!incidentNumber || !incidentDescription || !description || !tribeInformation) {
      alert("Please fill out all incident fields before classifying.");
      return;
    }
    setIsLoading(true);

    // 1. Check for Service Need (SN) FIRST — takes priority over alarm detection.
    // SN pattern can appear in either short description or description.
    const detectedServiceNeeds = Array.from(new Set(extractServiceNeeds(description).concat(extractServiceNeeds(incidentDescription))));

    if (detectedServiceNeeds.length > 0) {
      console.log('[IncidentView] Service Need (SN) detected in description');
      setServiceNeedMode(true);
      setAlarmMode(false);
      setDetectedAlarmName(null);
      setClassificationResult({
        service_need_detected: true,
        service_needs: detectedServiceNeeds,
      });
      setIsLoading(false);
      return;
    }

    // 2. Check if short description contains an alarm name.
    const alarmName = extractAlarmName(incidentDescription);
    if (alarmName) {
      console.log('[IncidentView] Alarm detected in short description:', alarmName);
      setDetectedAlarmName(alarmName);
      setAlarmMode(true);
      setServiceNeedMode(false);
      setClassificationResult({ alarm_detected: true });
      setIsLoading(false);
      return;
    }

    // No SN/alarm found — proceed with normal classification
    setAlarmMode(false);
    setServiceNeedMode(false);
    setDetectedAlarmName(null);

    try {
      // 1. Classify via AppSync GraphQL mutation
      const response = await graphqlMutation(classifyMutation, {
        input: {
          incident_number: incidentNumber,
          incident_description: incidentDescription,
          description: description,
        },
      });

      // Parse the LambdaResponse { statusCode, body(AWSJSON) }
      const lambdaResponse = response.classify;
      console.log('[Classify] Raw GraphQL response:', response);
      console.log('[Classify] lambdaResponse:', lambdaResponse);

      let data =
        typeof lambdaResponse.body === 'string'
          ? JSON.parse(lambdaResponse.body)
          : lambdaResponse.body;

      // Body may be double-encoded (a JSON string containing another JSON string)
      if (typeof data === 'string') {
        data = JSON.parse(data);
      }

      console.log('[Classify] Parsed data:', data);
      console.log('[Classify] dynamodb_results:', data?.dynamodb_results);

      if (data.error) {
        alert(data.error);
      } else {
        const results = data.dynamodb_results || [];
        const firstResult = results[0];
        let runbookSteps = firstResult?.runbook_steps || [];
        setHasAutomation(Number(firstResult?.is_automation) === 1);

        // Resolve the runbook keyword — classification may return it in
        // different fields depending on the backend version.
        const runbookKeyword = data.predicted_runbook || firstResult?.keyword || data.predicted_class || '';

        if (runbookKeyword) {
          // Legacy REST fallback removed — runbook steps are loaded via GraphQL below.
          console.log('[IncidentView] Runbook keyword resolved:', runbookKeyword);
        }

        // Load full runbook steps from GraphQL so scenario headers are included.
        // The classification API strips/rewrites step descriptions; the GraphQL
        // path returns exact DynamoDB content including "Scenario X:" header rows.
        // Try multiple keyword candidates since classification may return it
        // in different fields depending on the backend version.
        const keywordCandidates = [
          runbookKeyword,
          firstResult?.keyword,
          data.predicted_class,
          data.predicted_runbook,
        ].filter((k) => k && typeof k === 'string');

        let graphqlStepsLoaded = false;
        for (const candidate of keywordCandidates) {
          if (graphqlStepsLoaded) break;
          try {
            console.log('[IncidentView] Trying GraphQL getRunbook with keyword:', candidate);
            const rbResult = await graphqlQuery(getRunbook, { keyword: candidate });
            const rbSteps = rbResult?.getRunbook?.runbook_steps;
            if (rbSteps?.length > 0) {
              runbookSteps = rbSteps;
              graphqlStepsLoaded = true;
              console.log('[IncidentView] Full runbook loaded from GraphQL:', rbSteps.length, 'steps (keyword:', candidate, ')');
            }
          } catch (rbErr) {
            console.warn('[IncidentView] GraphQL getRunbook failed for keyword:', candidate, rbErr.message);
          }
        }

        // Refresh image URLs from full runbook steps when image keys are available.
        const refreshedResults = await refreshImageUrls(runbookSteps);
        setDynamodbResults(refreshedResults);
        setClassificationResult(data);

        // 2. Create incident record in DynamoDB via AppSync (first classify only).
        // UpdateIncidentInput only accepts feedback fields, not classification
        // fields, so we only create on first classify and skip DB write on re-classify.
        try {
          if (!incidentDatetime) {
            const datetime = new Date().toISOString();
            setIncidentDatetime(datetime);
            await graphqlMutation(createIncident, {
              input: {
                incident_number: incidentNumber,
                datetime: datetime,
                short_description: incidentDescription,
                description: description,
                predicted_class: data.predicted_class ?? null,
                confidence: data.confidence ?? null,
                is_confident: data.is_confident ?? null,
                ttl_time: Math.floor(Date.now() / 1000) + INCIDENT_TTL_SECONDS,
              },
            });
            console.log('[IncidentView] Incident record created in DynamoDB');
          } else {
            console.log('[IncidentView] Re-classify: skipping DB write (UpdateIncidentInput does not accept classification fields)');
          }
        } catch (incidentErr) {
          // Non-blocking: classification still succeeded even if incident record fails
          console.warn('[IncidentView] Could not create/update incident record:', incidentErr.message);
        }

        setActiveView(null);
        // Initialize default feedback: thumbs up for all steps and overall
        const defaultStepFeedback = {};
        refreshedResults.forEach((_, index) => {
          defaultStepFeedback[index] = { rating: 'up', reason: '', userClicked: false };
        });
        setStepFeedback(defaultStepFeedback);
        setOverallFeedback({ rating: 'up', reason: '', userClicked: false });
      }
    } catch (error) {
      console.error("Classification Error:", error);
      alert("An error occurred during classification.");
    } finally {
      setIsLoading(false);
    }
  };

  // --- Refresh Image URLs after expiry ---
  const refreshImageUrls = async (steps) => {
    if (!steps || steps.length === 0) return steps;
    try {
      const stepsWithExistingImageUrls = steps.some((step) => getStepImageUrl(step));
      const runbookSteps = steps
        .map((step, index) => ({
          step_number: getStepNumber(step, index),
          description: getStepDescription(step),
          image_s3_key: getStepImageKey(step),
        }))
        .filter((s) => s.image_s3_key);

      if (runbookSteps.length === 0) {
        if (!stepsWithExistingImageUrls) {
          console.info('[RefreshImageUrls] No image references found in steps. Skipping refresh.');
        }
        return steps;
      }

      const response = await graphqlMutation(refreshImageUrlsMutation, {
        input: {
          body: JSON.stringify({ runbook_steps: runbookSteps }),
        },
      });

      console.log('[RefreshImageUrls] Response:', response);

      // Parse the Lambda response
      const lambdaResponse = response.refreshImageUrls;
      let data =
        typeof lambdaResponse.body === 'string'
          ? JSON.parse(lambdaResponse.body)
          : lambdaResponse.body;

      // Handle double-encoded JSON if needed
      if (typeof data === 'string') {
        data = JSON.parse(data);
      }

      console.log('[RefreshImageUrls] Parsed data:', data);

      // Merge refreshed URLs back into original steps
      const refreshedSteps = steps.map((step, index) => {
        const refreshedStep = data.runbook_steps?.find(
          (rs) => rs.step_number === getStepNumber(step, index)
        );
        if (refreshedStep && (refreshedStep.image_url || refreshedStep.imageUrl)) {
          return {
            ...step,
            image_url: refreshedStep.image_url || refreshedStep.imageUrl,
          };
        }
        return step;
      });

      return refreshedSteps;
    } catch (err) {
      console.warn('[RefreshImageUrls] Error refreshing URLs:', err.message);
      // Return original steps if refresh fails
      return steps;
    }
  };

  // --- Old: handleFeedback (commented out) ---
  // const handleFeedback = async (feedback) => {
  //   if (!classificationResult) return;
  //   try {
  //     const topicFeedback = feedback === "Helpful" ? "Correct" : "Incorrect";
  //     const data = await apiCall("/feedback", {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify({
  //         incident_number: classificationResult.incident_number,
  //         topic_feedback: topicFeedback,
  //       }),
  //     });
  //     if (data.success) {
  //       if (feedback === "Helpful") {
  //         alert("Thanks for your feedback!");
  //         setIsProceedEnabled(true);
  //       } else {
  //         navigate('/select-runbook', {
  //           state: {
  //             tribe: tribeInformation,
  //             incident_number: classificationResult.incident_number,
  //             incident_description: incidentDescription,
  //             description: description,
  //           }
  //         });
  //       }
  //     } else {
  //       alert("Error submitting feedback.");
  //     }
  //   } catch (error) {
  //     console.error("Feedback Error:", error);
  //     alert("An error occurred while submitting feedback.");
  //   }
  // };

  // --- Old: handleGoToResolution (commented out) ---
  // const handleGoToResolution = () => {
  //   if (classificationResult) {
  //     const runbookId = classificationResult.predicted_runbook;
  //     const incident_number = classificationResult.incident_number;
  //     console.log(`[IncidentView] Navigating to runbook: ${runbookId}`);
  //     navigate(`/resolution?runbook_id=${runbookId}&incident_number=${incident_number}`);
  //   }
  // };

  // --- New: step-level feedback handlers ---
  const handleStepThumbsUp = (index) => {
    console.log(`[Feedback] Step ${index + 1} thumbs up`);
    setStepFeedback((prev) => ({
      ...prev,
      [index]: { rating: 'up', reason: '', userClicked: true },
    }));
  };

  const handleStepThumbsDown = (index) => {
    console.log(`[Feedback] Step ${index + 1} thumbs down`);
    setStepFeedback((prev) => ({
      ...prev,
      [index]: { ...prev[index], rating: 'down', reason: prev[index]?.reason || '', userClicked: true },
    }));
  };

  const handleStepReasonChange = (index, reason) => {
    console.log(`[Feedback] Step ${index + 1} reason updated:`, reason);
    setStepFeedback((prev) => ({
      ...prev,
      [index]: { ...prev[index], reason },
    }));
  };

  // --- New: overall feedback handlers ---
  const handleOverallThumbsUp = () => {
    console.log('[Feedback] Overall thumbs up');
    setOverallFeedback({ rating: 'up', reason: '', userClicked: true });
  };

  const handleOverallThumbsDown = () => {
    console.log('[Feedback] Overall thumbs down');
    setOverallFeedback((prev) => ({ ...prev, rating: 'down', userClicked: true }));
  };

  const handleOverallReasonChange = (reason) => {
    console.log('[Feedback] Overall reason updated:', reason);
    setOverallFeedback((prev) => ({ ...prev, reason }));
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (investigatePollRef.current) clearInterval(investigatePollRef.current);
      if (serviceNeedPollRef.current) clearInterval(serviceNeedPollRef.current);
    };
  }, []);

  // --- Helper: build the feedback payload for the updateIncident mutation ---
  // Reads from refs so it always has current values
  const buildFeedbackPayload = () => {
    const currentStepFeedback = stepFeedbackRef.current;
    const currentOverallFeedback = overallFeedbackRef.current;
    const currentDynamodbResults = dynamodbResultsRef.current;
    const currentIncidentNumber = incidentNumberRef.current;
    const currentIncidentDatetime = incidentDatetimeRef.current;

    const stepFeedbackData = Object.entries(currentStepFeedback).map(([idx, fb]) => {
      const step = currentDynamodbResults[Number(idx)];
      return {
        step_number: Number(idx),
        step_label: step?.['step_number'] || step?.['Step Number'] || `Step ${Number(idx) + 1}`,
        feedback: fb.rating === 'up' ? 1 : 0,
        feedback_reason: fb.reason || '',
      };
    });
    return {
      incident_number: currentIncidentNumber,
      datetime: currentIncidentDatetime,
      input: {
        overall_correctness:
          currentOverallFeedback.rating === 'up'
            ? 'Correct'
            : currentOverallFeedback.rating === 'down'
              ? 'Incorrect'
              : null,
        overall_text_feedback: currentOverallFeedback.reason || '',
        step_feedback: JSON.stringify(stepFeedbackData),
        feedback_source: 'manual',
      },
    };
  };

  // --- Map step keys to display messages ---
  const agentStepLabels = {
    step_1_primary_alarm_check: 'Alarm check in progress',
    step_2_alarm_history: 'Checking alarm history',
    step_3_log_analysis: 'Agent analyzing logs',
    step_4_dependent_alarms_check: 'Checking dependent alarms',
    step_5_small_failure_check: 'Checking if alarm resolved within threshold time (15mins)',
    final_result: 'Agent ran successfully please check your email for analysis',
  };

  // --- Poll step-poll API every 3 seconds ---
  const startStepPolling = () => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    seenIntermediateStep.current = false;
    pollErrorCount.current = 0;
    pollingRef.current = setInterval(async () => {
      try {
        const res = await fetch('https://k5jveugx31.execute-api.eu-west-1.amazonaws.com/dev/step-poll', {
          method: 'POST',
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const rawData = await res.json();
        console.log('[AI Agent Poll]', rawData);

        // Handle Lambda error responses (errorMessage means Lambda crashed)
        if (rawData.errorMessage) {
          console.warn('[AI Agent Poll] Lambda error:', rawData.errorMessage);
          pollErrorCount.current += 1;
          if (pollErrorCount.current >= 10) {
            console.error('[AI Agent Poll] Too many Lambda errors, stopping poll.');
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            setAiAgentStatus('error');
          }
          return;
        }

        // Reset error count on successful response
        pollErrorCount.current = 0;

        // Lambda proxy responses wrap the payload in a `body` JSON string
        const data = (typeof rawData.body === 'string') ? JSON.parse(rawData.body) : rawData;
        const step = data.current_step;
        if (!step) return;

        // If we get final_result but haven't seen any intermediate step yet,
        // it's stale data from a previous run — skip it
        if (step === 'final_result' && !seenIntermediateStep.current) {
          console.log('[AI Agent Poll] Ignoring stale final_result, waiting for fresh steps...');
          return;
        }

        // Track that we've seen a real intermediate step
        if (step !== 'final_result') {
          seenIntermediateStep.current = true;
        }

        setCurrentAgentStep(step);

        // Stop polling when agent is finished
        if (step === 'final_result') {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setAiAgentStatus('done');
        }
      } catch (err) {
        console.error('[AI Agent Poll] Error:', err);
        pollErrorCount.current += 1;
        if (pollErrorCount.current >= 10) {
          console.error('[AI Agent Poll] Too many errors, stopping poll.');
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setAiAgentStatus('error');
        }
      }
    }, 100);
  };

  // --- AI Agent handler: trigger + poll ---
  const handleAiAgent = async () => {
    setActiveView('aiAgent');
    setAiAgentStatus('working');
    setCurrentAgentStep(null);
    if (pollingRef.current) clearInterval(pollingRef.current);

    // Start polling immediately (don't wait for trigger to respond)
    startStepPolling();

    try {
      // Trigger the agent (fire concurrently with polling)
      const res = await fetch('https://k5jveugx31.execute-api.eu-west-1.amazonaws.com/dev/trigger-agent', {
        method: 'POST',
      });
      console.log('[AI Agent] Trigger response:', res.status, res.statusText);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (err) {
      console.error('[AI Agent] Error:', err.name, '-', err.message);
      if (err.name === 'TypeError') {
        console.error('[AI Agent] Network/CORS error — check that the API Gateway has CORS enabled for this origin.');
      }
      // Stop polling and show error
      if (pollingRef.current) clearInterval(pollingRef.current);
      pollingRef.current = null;
      setAiAgentStatus('error');
    }
  };

  // --- Find which session id (if any) owns a PLAN entry ---
  // The planner writes the PLAN keyed by the session_id it ran under, which is
  // not always the sessionId returned by triggerDevopsAgent — the orchestrator
  // may use a separate agent_session. Check each candidate id and return the
  // first that has a genuine PLAN entry, so the "View Plan" button links to the
  // session that actually owns the plan.
  const findPlanSession = useCallback(async (candidateIds = []) => {
    const ids = [...new Set(candidateIds.filter(Boolean))];
    for (const id of ids) {
      try {
        const res = await graphqlQuery(getSessionTimeline, { sessionId: id, entryType: 'PLAN' });
        // Defensive: the backend `entryType` filter is not always honored, so
        // filter client-side to genuine PLAN entries.
        const hasPlan = (res?.getSessionTimeline?.items || []).some(
          (e) => e?.entryType === 'PLAN'
        );
        if (hasPlan) return id;
      } catch (err) {
        console.warn('[Investigate] PLAN lookup failed for', id, '-', err.message);
      }
    }
    return null;
  }, []);

  // --- Plan Agent: invoke the orchestrator, then poll devops-orchestrator-sessions for PLAN ---
  // The invokeAgent call may timeout (agent takes >30s), but that's OK — the
  // orchestrator still runs in the background. We poll getSessionTimeline for a
  // PLAN entry to appear in the devops-orchestrator-sessions DynamoDB table.
  const handleViewPlan = useCallback(async () => {
    const t0 = Date.now();
    console.log('[PlanAgent] ===== View Plan clicked =====', new Date(t0).toISOString());
    console.log('[PlanAgent] earlyPlanSessionIdRef:', earlyPlanSessionIdRef.current, '| planAgentStatus:', planAgentStatus);
    console.log('[PlanAgent] Current state:', {
      detectedAlarmName,
      incidentDescription,
      tribeInformation,
      investigateS3Path,
    });

    // If the early orchestrator call (fired at investigation completion) already
    // has a plan ready, navigate immediately without re-invoking the agent.
    if (earlyPlanSessionIdRef.current && planAgentStatus === 'ready') {
      console.log('[PlanAgent] ✅ Plan already ready from early call — navigating immediately to', `/devops/sessions/${earlyPlanSessionIdRef.current}/plan`);
      navigate(`/devops/sessions/${earlyPlanSessionIdRef.current}/plan`);
      return;
    }

    // If the early call is still polling, stop it and reuse its sessionId so we
    // don't duplicate the orchestrator invocation.
    if (earlyPlanSessionIdRef.current && planAgentStatus === 'polling') {
      console.log('[PlanAgent] Early poll still in progress — reusing earlySessionId:', earlyPlanSessionIdRef.current);
      // Stop the background poll; handleViewPlan takes ownership and polls below.
      if (planAgentPollRef.current) { clearInterval(planAgentPollRef.current); planAgentPollRef.current = null; }
      setPlanAgentError('');
      setPlanAgentStatus('polling');
      const reuseSessionId = earlyPlanSessionIdRef.current;
      let pollCount = 0;
      planAgentPollRef.current = setInterval(async () => {
        pollCount += 1;
        const elapsedSec = ((Date.now() - t0) / 1000).toFixed(1);
        console.log(`[PlanAgent] (reuse) Poll #${pollCount} (${elapsedSec}s) session=${reuseSessionId}`);
        try {
          // Check for FAILED status early so the user isn't waiting the full timeout
          const timelineRes = await graphqlQuery(getSessionTimeline, { sessionId: reuseSessionId });
          const allItems = timelineRes?.getSessionTimeline?.items || [];
          const statusItems = (allItems).filter((e) => e?.entryType === 'STATUS');
          const latestStatus = statusItems.slice().sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0];
          if (latestStatus?.data) {
            try {
              const parsed = typeof latestStatus.data === 'string' ? JSON.parse(latestStatus.data) : latestStatus.data;
              const state = (parsed?.status || latestStatus.status || '').toUpperCase();
              if (state === 'FAILED') {
                clearInterval(planAgentPollRef.current); planAgentPollRef.current = null;
                console.error(`[PlanAgent] ❌ (reuse) Backend FAILED after ${pollCount} polls`);
                setPlanAgentStatus('error');
                setPlanAgentError(parsed?.error || 'Plan agent failed. Please try again.');
                earlyPlanSessionIdRef.current = null;
                return;
              }
            } catch { /* ignore parse errors */ }
          }

          const planItems = allItems.filter((e) => e?.entryType === 'PLAN');
          console.log(`[PlanAgent] (reuse) Poll #${pollCount}: ${planItems.length} PLAN entries (${allItems.length} total)`);
          if (planItems.length > 0) {
            clearInterval(planAgentPollRef.current); planAgentPollRef.current = null;
            console.log(`[PlanAgent] ✅ (reuse) PLAN found after ${pollCount} polls — navigating`);
            setPlanAgentStatus('ready');
            navigate(`/devops/sessions/${reuseSessionId}/plan`);
            return;
          }

          // Check for APPROVAL_RESPONSE with ALLOW — generic check
          const approvalItems = allItems.filter((e) => e?.entryType === 'APPROVAL_RESPONSE');
          const allowEntry = approvalItems.find((e) => {
            try {
              const data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
              return (data?.decision || '').toUpperCase() === 'ALLOW';
            } catch { return false; }
          });
          if (allowEntry) {
            clearInterval(planAgentPollRef.current); planAgentPollRef.current = null;
            console.log(`[PlanAgent] ✅ (reuse) ALLOW found after ${pollCount} polls`);
            console.log('[PlanAgent]', getApprovalMessage('ALLOW', reuseSessionId));
            setPlanAgentStatus('ready');
            navigate(`/devops/sessions/${reuseSessionId}/plan`);
            return;
          }
          if (Date.now() - t0 > 600000) {
            clearInterval(planAgentPollRef.current); planAgentPollRef.current = null;
            console.error('[PlanAgent] ❌ (reuse) Poll timed out');
            setPlanAgentStatus('error');
            setPlanAgentError('Plan generation timed out. Please try again.');
            earlyPlanSessionIdRef.current = null;
          }
        } catch (e) { console.warn('[PlanAgent] (reuse) poll error:', e.message); }
      }, 4000);
      return;
    }

    // No early call in progress (re-run scenario or no s3_path was available) —
    // fall through to the standard fresh invocation path.
    console.log('[PlanAgent] No usable early session — will invoke orchestrator fresh');

    // Clear any stale poll
    if (planAgentPollRef.current) {
      clearInterval(planAgentPollRef.current);
      planAgentPollRef.current = null;
    }
    setPlanAgentError('');
    setPlanAgentStatus('invoking');
    console.log('[PlanAgent] Status -> invoking');

    const shortDescription = detectedAlarmName
      ? `ALARM: ${detectedAlarmName}`
      : incidentDescription || '';

    const s3Payload = investigateS3Path || '';
    if (!s3Payload) {
      console.error('[PlanAgent] No S3 path available. investigateS3Path:', investigateS3Path);
      setPlanAgentStatus('error');
      setPlanAgentError('No investigation report S3 path available. Please run the investigation first.');
      return;
    }

    console.log('[PlanAgent] Building invokeAgent input:');
    console.log('[PlanAgent]   short_description:', shortDescription);
    console.log('[PlanAgent]   tribe:', tribeInformation || '(not set)');
    console.log('[PlanAgent]   payload (S3 path):', s3Payload);

    // Reuse the investigation's session id so the Plan step is correlated
    // with the investigation. Fall back to a fresh id only if Plan is reached
    // without a completed investigation.
    const requestedSessionId = investigateSessionId || `ui-plan-${crypto.randomUUID()}`;
    let pollSessionId = requestedSessionId;
    earlyPlanSessionIdRef.current = requestedSessionId;
    console.log('[PlanAgent] Generated fresh sessionId:', requestedSessionId, `(${requestedSessionId.length} chars)`);

    const invokeInput = {
      short_description: shortDescription,
      tribe: tribeInformation || undefined,
      payload: s3Payload,
      sessionId: requestedSessionId,
      // ServiceNow ID from the Incident Resolver page - keys the agent-run
      // incident-table row to match this incident's other records.
      incident_number: incidentNumber || undefined,
    };
    console.log('[PlanAgent] Full mutation input:', JSON.stringify(invokeInput, null, 2));

    // Fire invokeAgent — don't await it. The resolver will timeout (~30s) but the
    // orchestrator still runs and writes to devops-orchestrator-sessions.
    // Expected response status: "PROCESSING" (async) or "FAILED".
    console.log('[PlanAgent] Calling invokeAgent mutation (fire-and-forget)...');

    graphqlMutation(invokeAgent, { input: invokeInput })
      .then((res) => {
        const response = res?.invokeAgent;
        console.log('[PlanAgent] invokeAgent resolved:', JSON.stringify(response));
        // Use backend sessionId for all timeline polling when provided.
        if (response?.sessionId && response.sessionId !== pollSessionId) {
          console.log('[PlanAgent] Backend returned different sessionId. Switching polling sessionId from', pollSessionId, 'to', response.sessionId);
          pollSessionId = response.sessionId;
          earlyPlanSessionIdRef.current = response.sessionId;
        }
        if (response?.status === 'FAILED') {
          console.error('[PlanAgent] Backend returned FAILED:', response.error);
          // Always stop polling and show error
          if (planAgentPollRef.current) {
            clearInterval(planAgentPollRef.current);
            planAgentPollRef.current = null;
          }
          setPlanAgentStatus('error');
          setPlanAgentError(
            response.error || 'The orchestrator failed to start.'
          );
        }
      })
      .catch((invokeErr) => {
        // Timeout expected — orchestrator runs longer than AppSync 30s limit
        console.warn('[PlanAgent] invokeAgent error (expected if timeout):', invokeErr.message);
      });

    console.log('[PlanAgent] Using sessionId for polling:', pollSessionId);

    // --- Poll getSessionTimeline for a PLAN entry ---
    setPlanAgentStatus('polling');
    console.log(`[PlanAgent] Status -> polling. Polling getSessionTimeline(${pollSessionId}) every 4s for PLAN entry...`);

    let pollCount = 0;
    planAgentPollRef.current = setInterval(async () => {
      pollCount += 1;
      const elapsedSec = ((Date.now() - t0) / 1000).toFixed(1);
      console.log(`[PlanAgent] Poll #${pollCount} (${elapsedSec}s) for session=${pollSessionId}`);

      try {
        // Fetch full timeline in one call — check for PLAN, terminal STATUS,
        // and code-review STEP_RESULT.
        const timelineRes = await graphqlQuery(getSessionTimeline, { sessionId: pollSessionId });
        const allItems = timelineRes?.getSessionTimeline?.items || [];
        const planItems = allItems.filter((e) => e?.entryType === 'PLAN');
        const statusItems = allItems.filter((e) => e?.entryType === 'STATUS');
        console.log(`[PlanAgent] Poll #${pollCount}: ${planItems.length} PLAN, ${statusItems.length} STATUS entries (${allItems.length} total)`);

        // PLAN entry found — navigate immediately
        if (planItems.length > 0) {
          clearInterval(planAgentPollRef.current);
          planAgentPollRef.current = null;
          const totalMs = Date.now() - t0;
          console.log(`[PlanAgent] ✅ PLAN found after ${pollCount} polls / ${totalMs}ms`);
          console.log('[PlanAgent] Navigating to:', `/devops/sessions/${pollSessionId}/plan`);
          setPlanAgentStatus('ready');
          navigate(`/devops/sessions/${pollSessionId}/plan`);
          return;
        }

        // Check for APPROVAL_RESPONSE with ALLOW — generic check for any agent flow
        const approvalItems = allItems.filter((e) => e?.entryType === 'APPROVAL_RESPONSE');
        if (approvalItems.length > 0) {
          const allowEntry = approvalItems.find((e) => {
            try {
              const data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
              return (data?.decision || '').toUpperCase() === 'ALLOW';
            } catch { return false; }
          });
          if (allowEntry) {
            clearInterval(planAgentPollRef.current);
            planAgentPollRef.current = null;
            const totalMs = Date.now() - t0;
            console.log(`[PlanAgent] ✅ ALLOW found after ${pollCount} polls / ${totalMs}ms`);
            console.log('[PlanAgent]', getApprovalMessage('ALLOW', pollSessionId));
            setPlanAgentStatus('ready');
            navigate(`/devops/sessions/${pollSessionId}/plan`);
            return;
          }
        }

        // Check STATUS entries for terminal or failed states
        const latestStatusEntry = statusItems.slice().sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0];
        if (latestStatusEntry?.data) {
          try {
            const data = typeof latestStatusEntry.data === 'string' ? JSON.parse(latestStatusEntry.data) : latestStatusEntry.data;
            const statusVal = (data?.status || data?.state || latestStatusEntry.status || '').toUpperCase();
            if (statusVal === 'FAILED') {
              clearInterval(planAgentPollRef.current);
              planAgentPollRef.current = null;
              console.error(`[PlanAgent] ❌ Orchestrator FAILED after ${pollCount} polls`);
              setPlanAgentStatus('error');
              setPlanAgentError(data?.error || data?.message || 'The orchestrator failed to generate a plan.');
              return;
            }
            // If status is a terminal completed state, navigate even if no PLAN/STEP_RESULT yet
            const PLAN_TERMINAL_STATES = ['COMPLETED', 'DONE', 'SUCCESS', 'COMPLETE'];
            if (PLAN_TERMINAL_STATES.includes(statusVal)) {
              clearInterval(planAgentPollRef.current);
              planAgentPollRef.current = null;
              console.log(`[PlanAgent] ✅ Terminal STATUS ${statusVal} after ${pollCount} polls — navigating`);
              setPlanAgentStatus('ready');
              navigate(`/devops/sessions/${pollSessionId}/plan`);
              return;
            }
          } catch { /* ignore parse errors */ }
        }

        // Timeout after 10 minutes of polling
        if (Date.now() - t0 > 600000) {
          clearInterval(planAgentPollRef.current);
          planAgentPollRef.current = null;
          console.error('[PlanAgent] ❌ Polling timed out after 10 minutes');
          setPlanAgentStatus('error');
          setPlanAgentError('Plan generation timed out. The orchestrator may still be running — try refreshing.');
          return;
        }
      } catch (pollErr) {
        console.warn(`[PlanAgent] Poll #${pollCount} error:`, pollErr.message);
      }
    }, 4000);
  }, [incidentDescription, investigateS3Path, investigateSessionId, tribeInformation, detectedAlarmName, navigate, planAgentStatus, incidentNumber]);

  // Clean up the plan-agent poller on unmount.
  useEffect(() => {
    return () => {
      if (planAgentPollRef.current) {
        console.log('[PlanAgent] Component unmounting — clearing poll interval.');
        clearInterval(planAgentPollRef.current);
      }
    };
  }, []);  // --- Investigate (DevOps Agent) handler ---
  const handleInvestigate = async (forceRefresh = false) => {
    const t0 = Date.now();
    console.log('[Investigate] ===== Investigate clicked =====', new Date(t0).toISOString());
    console.log('[Investigate] alarmMode:', alarmMode, '| detectedAlarmName:', detectedAlarmName, '| forceRefresh:', forceRefresh);

    setActiveView('investigate');
    setInvestigateStatus('working');
    setInvestigateMessage('Starting DevOps Agent...');
    setInvestigateReport('');
    setInvestigateS3Path(null);
    setHasPlanEntry(false);
    setPlanSessionId(null);
    setPlanAgentStatus(null);
    setPlanAgentError('');
    earlyPlanSessionIdRef.current = null;
    setDevopsFeedback({ rating: null, comment: '' });
    setDevopsFeedbackSubmitted(false);
    setDevopsFeedbackError(null);
    if (investigatePollRef.current) clearInterval(investigatePollRef.current);
    if (planAgentPollRef.current) { clearInterval(planAgentPollRef.current); planAgentPollRef.current = null; }

    // Backend requires alarmName to be non-null String.
    const alarmName = detectedAlarmName || incidentDescription || 'UNKNOWN_ALARM';

    try {
      console.log('[Investigate] Calling triggerDevopsAgent with alarmName:', alarmName);
      const response = await graphqlMutation(triggerDevopsAgent, { alarmName, incidentNumber: incidentNumber || undefined, forceRefresh: forceRefresh || undefined });
      const { sessionId } = response.triggerDevopsAgent;
      setInvestigateSessionId(sessionId);
      console.log('[Investigate] Agent triggered, sessionId:', sessionId);

      // Poll every 4 seconds with detailed logging
      const pollStartTime = Date.now();
      const MAX_POLL_DURATION_MS = 20 * 60 * 1000; // 20 minutes
      let pollCount = 0;
      let pollErrorCount = 0;
      let lastStatus = '';

      console.log(`[Investigate] Status -> polling. Polling checkDevopsStatus(${sessionId}) every 4s...`);

      investigatePollRef.current = setInterval(async () => {
        pollCount += 1;
        const elapsedSec = ((Date.now() - pollStartTime) / 1000).toFixed(1);

        // Timeout check
        if (Date.now() - pollStartTime > MAX_POLL_DURATION_MS) {
          console.warn(`[Investigate] ⏱️ Polling timed out after ${pollCount} polls / ${elapsedSec}s`);
          clearInterval(investigatePollRef.current);
          investigatePollRef.current = null;
          setInvestigateStatus('error');
          setInvestigateMessage('Investigation timed out after 20 minutes. The agent may still be working — please try again later.');
          return;
        }

        console.log(`[Investigate] Poll #${pollCount} (${elapsedSec}s) session=${sessionId}`);

        try {
          const pollRes = await graphqlQuery(checkDevopsStatus, { sessionId });
          pollErrorCount = 0; // reset on success
          const result = pollRes.checkDevopsStatus;
          const { status, message, report_url, s3_path, agent_session } = result;
          const statusUpper = (status || '').toUpperCase();

          // Log status transitions
          if (statusUpper !== lastStatus) {
            console.log(`[Investigate] Poll #${pollCount}: status changed ${lastStatus || '(none)'} → ${statusUpper} (${elapsedSec}s)`);
            lastStatus = statusUpper;
          } else {
            console.log(`[Investigate] Poll #${pollCount}: status=${statusUpper} message="${message || ''}"`);
          }

          setInvestigateMessage(message || 'Agent is working...');

          // Use pre-signed report_url if available, fall back to s3_path
          const reportUrl = report_url || s3_path;
          const isDone = statusUpper === 'COMPLETED' || (reportUrl && reportUrl.trim() !== '');

          if (isDone) {
            const totalMs = Date.now() - pollStartTime;
            console.log(`[Investigate] ✅ Investigation complete after ${pollCount} polls / ${totalMs}ms. status=${statusUpper}`);
            clearInterval(investigatePollRef.current);
            investigatePollRef.current = null;

            // Resolve which session id actually owns the PLAN entry once the
            // investigation completes, to avoid extra timeline queries per poll tick.
            const owner = await findPlanSession([sessionId, agent_session]);
            setHasPlanEntry(!!owner);
            setPlanSessionId(owner);

            // Store the S3 path for use by the Plan Agent
            if (s3_path) {
              console.log('[Investigate] Storing S3 path for Plan Agent:', s3_path);
              setInvestigateS3Path(s3_path);
            } else {
              console.warn('[Investigate] No s3_path in poll result — Plan Agent will not have a report path.');
            }

            // Fetch the markdown report using pre-signed URL
            if (reportUrl) {
              try {
                console.log('[Investigate] Fetching report from:', reportUrl);
                const reportRes = await fetch(reportUrl);
                if (reportRes.ok) {
                  const mdContent = await reportRes.text();
                  console.log(`[Investigate] Report loaded, length: ${mdContent.length} (${totalMs}ms total)`);
                  setInvestigateReport(mdContent);
                } else {
                  console.error('[Investigate] Failed to fetch report:', reportRes.status, reportRes.statusText);
                  setInvestigateReport(`## Report Generated\n\nThe report was generated but could not be loaded directly (HTTP ${reportRes.status}).\n\n**Download it here:** [Open Report](${reportUrl})`);
                }
              } catch (fetchErr) {
                console.error('[Investigate] Error fetching report:', fetchErr);
                setInvestigateReport(`## Report Generated\n\nThe report could not be displayed inline.\n\n**Download it here:** [Open Report](${reportUrl})`);
              }
            } else {
              setInvestigateReport('Investigation completed but no report URL was returned.');
            }
            setInvestigateStatus('done');
          } else if (statusUpper === 'FAILED') {
            const totalMs = Date.now() - pollStartTime;
            console.error(`[Investigate] ❌ Investigation FAILED after ${pollCount} polls / ${totalMs}ms. message="${message}"`);
            clearInterval(investigatePollRef.current);
            investigatePollRef.current = null;
            setInvestigateStatus('error');
            setInvestigateMessage(message || 'The DevOps Agent failed to generate a report.');
          }
        } catch (pollErr) {
          pollErrorCount += 1;
          const elapsedOnErr = ((Date.now() - pollStartTime) / 1000).toFixed(1);
          console.error(`[Investigate] Poll #${pollCount} error (consecutive: ${pollErrorCount}, ${elapsedOnErr}s):`, pollErr.message);
          if (pollErrorCount >= 5) {
            console.error(`[Investigate] ❌ Too many consecutive poll errors (${pollErrorCount}). Stopping.`);
            clearInterval(investigatePollRef.current);
            investigatePollRef.current = null;
            setInvestigateStatus('error');
            setInvestigateMessage('Lost connection to the investigation service. Please try again.');
          }
        }
      }, 4000);
    } catch (err) {
      console.error('[Investigate] Error triggering agent:', err);
      setInvestigateStatus('error');
      setInvestigateMessage('Failed to trigger the DevOps Agent: ' + err.message);
    }
  };

  // --- AWS Incident Investigation handler ---
  const handleAwsInvestigate = async (forceRefresh = false) => {
    const t0 = Date.now();
    console.log('[AWS Investigate] ===== Investigate clicked =====', new Date(t0).toISOString());
    console.log('[AWS Investigate] incidentNumber:', incidentNumber, '| forceRefresh:', forceRefresh);

    setActiveView('investigate');
    setInvestigateStatus('working');
    setInvestigateMessage('Starting DevOps Agent for AWS incident...');
    setInvestigateReport('');
    setInvestigateS3Path(null);
    setHasPlanEntry(false);
    setPlanSessionId(null);
    setPlanAgentStatus(null);
    setPlanAgentError('');
    earlyPlanSessionIdRef.current = null;
    setDevopsFeedback({ rating: null, comment: '' });
    setDevopsFeedbackSubmitted(false);
    setDevopsFeedbackError(null);
    if (investigatePollRef.current) clearInterval(investigatePollRef.current);
    if (planAgentPollRef.current) { clearInterval(planAgentPollRef.current); planAgentPollRef.current = null; }

    try {
      console.log('[AWS Investigate] Calling triggerDevopsInvestigation with:', {
        incident_number: incidentNumber,
        incident_description: description,
        short_description: incidentDescription,
        infrastructure_type: 'AWS',
      });
      const response = await graphqlMutation(triggerDevopsInvestigation, {
        input: {
          incident_number: incidentNumber,
          incident_description: description,
          short_description: incidentDescription || '',
          infrastructure_type: 'AWS',
        },
      });
      const { sessionId } = response.triggerDevopsInvestigation;
      setInvestigateSessionId(sessionId);
      console.log('[AWS Investigate] Agent triggered, sessionId:', sessionId);

      // Poll every 4 seconds with SN-style logging
      const pollStartTime = Date.now();
      const MAX_POLL_DURATION_MS = 20 * 60 * 1000; // 20 minutes
      let pollCount = 0;
      let pollErrorCount = 0;
      let lastStatus = '';

      console.log(`[AWS Investigate] Status -> polling. Polling checkDevopsStatus(${sessionId}) every 4s...`);

      investigatePollRef.current = setInterval(async () => {
        pollCount += 1;
        const elapsedSec = ((Date.now() - pollStartTime) / 1000).toFixed(1);

        // Timeout check
        if (Date.now() - pollStartTime > MAX_POLL_DURATION_MS) {
          console.warn(`[AWS Investigate] ⏱️ Polling timed out after ${pollCount} polls / ${elapsedSec}s`);
          clearInterval(investigatePollRef.current);
          investigatePollRef.current = null;
          setInvestigateStatus('error');
          setInvestigateMessage('Investigation timed out after 20 minutes. The agent may still be working — please try again later.');
          return;
        }

        console.log(`[AWS Investigate] Poll #${pollCount} (${elapsedSec}s) session=${sessionId}`);

        try {
          const pollRes = await graphqlQuery(checkDevopsStatus, { sessionId });
          pollErrorCount = 0; // reset on success
          const result = pollRes.checkDevopsStatus;
          const { status, message, report_url, s3_path } = result;
          const statusUpper = (status || '').toUpperCase();

          // Log status transitions
          if (statusUpper !== lastStatus) {
            console.log(`[AWS Investigate] Poll #${pollCount}: status changed ${lastStatus || '(none)'} → ${statusUpper} (${elapsedSec}s)`);
            lastStatus = statusUpper;
          } else {
            console.log(`[AWS Investigate] Poll #${pollCount}: status=${statusUpper} message="${message || ''}"`);
          }

          setInvestigateMessage(message || 'Agent is working...');

          // Use pre-signed report_url if available, fall back to s3_path
          const reportUrl = report_url || s3_path;
          const isDone = statusUpper === 'COMPLETED' || statusUpper === 'CACHED' || (reportUrl && reportUrl.trim() !== '');

          if (isDone) {
            const totalMs = Date.now() - pollStartTime;
            console.log(`[AWS Investigate] ✅ Investigation complete after ${pollCount} polls / ${totalMs}ms. status=${statusUpper}`);
            clearInterval(investigatePollRef.current);
            investigatePollRef.current = null;

            // Store the S3 path for use by the Plan Agent
            if (s3_path) {
              console.log('[AWS Investigate] Storing S3 path for Plan Agent:', s3_path);
              setInvestigateS3Path(s3_path);
            }

            // Fetch the markdown report using pre-signed URL
            if (reportUrl) {
              try {
                console.log('[AWS Investigate] Fetching report from:', reportUrl);
                const reportRes = await fetch(reportUrl);
                if (reportRes.ok) {
                  const mdContent = await reportRes.text();
                  console.log('[AWS Investigate] Report loaded, length:', mdContent.length);
                  setInvestigateReport(mdContent);
                } else {
                  console.error('[AWS Investigate] Failed to fetch report:', reportRes.status, reportRes.statusText);
                  setInvestigateReport(`## Report Generated\n\nThe report was generated but could not be loaded directly (HTTP ${reportRes.status}).\n\n**Download it here:** [Open Report](${reportUrl})`);
                }
              } catch (fetchErr) {
                console.error('[AWS Investigate] Error fetching report:', fetchErr);
                setInvestigateReport(`## Report Generated\n\nThe report could not be displayed inline.\n\n**Download it here:** [Open Report](${reportUrl})`);
              }
            } else {
              setInvestigateReport('Investigation completed but no report URL was returned.');
            }
            setInvestigateStatus('done');
          } else if (statusUpper === 'FAILED') {
            const totalMs = Date.now() - pollStartTime;
            console.error(`[AWS Investigate] ❌ Investigation FAILED after ${pollCount} polls / ${totalMs}ms. message="${message}"`);
            clearInterval(investigatePollRef.current);
            investigatePollRef.current = null;
            setInvestigateStatus('error');
            setInvestigateMessage(message || 'The DevOps Agent failed to generate a report.');
          }
        } catch (pollErr) {
          pollErrorCount += 1;
          const elapsedOnErr = ((Date.now() - pollStartTime) / 1000).toFixed(1);
          console.error(`[AWS Investigate] Poll #${pollCount} error (consecutive: ${pollErrorCount}, ${elapsedOnErr}s):`, pollErr.message);
          if (pollErrorCount >= 5) {
            console.error(`[AWS Investigate] ❌ Too many consecutive poll errors (${pollErrorCount}). Stopping.`);
            clearInterval(investigatePollRef.current);
            investigatePollRef.current = null;
            setInvestigateStatus('error');
            setInvestigateMessage('Lost connection to the investigation service. Please try again.');
          }
        }
      }, 4000);
    } catch (err) {
      console.error('[AWS Investigate] Error triggering agent:', err);
      setInvestigateStatus('error');
      setInvestigateMessage('Failed to trigger the DevOps Agent: ' + err.message);
    }
  };

  // --- Copy code block to clipboard ---
  const handleCopyCode = useCallback((code, blockIndex) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedBlock(blockIndex);
      setTimeout(() => setCopiedBlock(null), 2000);
    });
  }, []);

  // --- Show a toast passed via navigation state (e.g. "Plan rejected") ---
  useEffect(() => {
    const incomingToast = location.state?.toast;
    if (incomingToast) {
      setToastMessage(incomingToast);
      // Clear the navigation state so the toast doesn't reappear on refresh/back.
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  // --- Auto-dismiss toast after a few seconds ---
  useEffect(() => {
    if (!toastMessage) return undefined;
    const timer = setTimeout(() => setToastMessage(null), 5000);
    return () => clearTimeout(timer);
  }, [toastMessage]);

  // --- Submit DevOps Agent Feedback ---
  const handleSubmitDevopsFeedback = async () => {
    if (!devopsFeedback.rating) return;
    setIsSubmittingDevopsFeedback(true);
    setDevopsFeedbackError(null);
    try {
      // Create incident record first if one doesn't exist yet (alarm/investigate flow)
      let datetime = incidentDatetime;
      if (!datetime) {
        datetime = new Date().toISOString();
        console.log('[DevOps Feedback] No existing incident record — creating one:', {
          incident_number: incidentNumber,
          datetime,
          short_description: incidentDescription,
        });
        try {
          await graphqlMutation(createIncident, {
            input: {
              incident_number: incidentNumber,
              datetime,
              short_description: incidentDescription || '',
              description: description || '',
              ttl_time: Math.floor(Date.now() / 1000) + INCIDENT_TTL_SECONDS,
            },
          });
          setIncidentDatetime(datetime);
          console.log('[DevOps Feedback] Incident record created successfully');
        } catch (createErr) {
          console.error('[DevOps Feedback] Failed to create incident record:', createErr);
          throw createErr;
        }
      } else {
        console.log('[DevOps Feedback] Using existing incident record:', { incident_number: incidentNumber, datetime });
      }

      const feedbackPayload = {
        input: {
          incident_number: incidentNumber,
          datetime,
          short_description: incidentDescription || '',
          description: devopsFeedback.comment || '',
          agent_feedback: devopsFeedback.rating,
        },
      };
      console.log('[DevOps Feedback] Submitting feedback to backend:', feedbackPayload);
      await graphqlMutation(submitDevopsAgentFeedback, feedbackPayload);
      console.log('[DevOps Feedback] Feedback submitted successfully');
      setDevopsFeedbackSubmitted(true);
    } catch (err) {
      console.error('[DevOps Feedback] Error:', err);
      setDevopsFeedbackError('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmittingDevopsFeedback(false);
    }
  };

  // --- Shared Investigate View renderer ---
  const renderInvestigateView = () => {
    if (activeView !== 'investigate') return null;
    return (
      <div>
        {investigateStatus === 'working' && (
          <div className="card p-4 text-center">
            <div className="spinner-border text-primary mb-3" role="status" />
            <h5>DevOps Agent is Investigating...</h5>
            <p className="text-muted mt-2">{investigateMessage}</p>
          </div>
        )}

        {investigateStatus === 'error' && (
          <div className="alert alert-danger">
            <strong>Investigation Failed:</strong> {investigateMessage}
          </div>
        )}

        {investigateStatus === 'done' && investigateReport && (
          <div className="investigate-report">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={{
                code({ node, inline, className, children, ...props }) {
                  const match = /language-(\w+)/.exec(className || '');
                  const codeString = String(children).replace(/\n$/, '');
                  if (!inline && (match || codeString.includes('\n'))) {
                    const blockIndex = codeString.substring(0, 40);
                    return (
                      <div className="code-block-wrapper">
                        {match && <span className="code-lang-badge">{match[1]}</span>}
                        <button
                          className={`copy-btn ${copiedBlock === blockIndex ? 'copied' : ''}`}
                          onClick={() => handleCopyCode(codeString, blockIndex)}
                          title="Copy to clipboard"
                        >
                          {copiedBlock === blockIndex ? '✓ Copied' : '📋 Copy'}
                        </button>
                        <pre>
                          <code className={className} {...props}>
                            {children}
                          </code>
                        </pre>
                      </div>
                    );
                  }
                  return (
                    <code className={className} {...props}>
                      {children}
                    </code>
                  );
                },
              }}
            >
              {investigateReport}
            </ReactMarkdown>
          </div>
        )}

        {investigateStatus === 'done' && !investigateReport && (
          <div className="alert alert-warning">
            Investigation completed but no report was generated.
          </div>
        )}

        {/* Re-run & Feedback section */}
        {investigateStatus === 'done' && (
          <div className="card p-3 mt-3" style={{ backgroundColor: '#f8f9fa' }}>
            <div className="d-flex align-items-center gap-2 mb-3">
              <button
                type="button"
                className="kone-btn kone-btn-outline"
                onClick={() => handleInvestigate(true)}
                title="Re-run investigation (bypasses cache and fetches fresh results)"
              >
                🔄 Re-run Investigation
              </button>
              {investigateStatus === 'done' && environmentType !== 'AWS' && (
                <button
                  type="button"
                  className="kone-btn kone-btn-primary"
                  onClick={handleViewPlan}
                  disabled={
                    !canInvokeCEPAgent() ||
                    planAgentStatus === 'invoking' ||
                    planAgentStatus === 'polling'
                  }
                  title="Generate an investigation plan and review it for approval"
                >
                  {!canInvokeCEPAgent()
                    ? '🔒 No Access'
                    : planAgentStatus === 'invoking' || planAgentStatus === 'polling'
                      ? '⏳ Generating Plan...'
                      : '📋 View Plan'}
                </button>
              )}
              {planAgentError && environmentType !== 'AWS' && (
                <span className="text-danger ms-2"><small>{planAgentError}</small></span>
              )}
            </div>

            <h5 className="kone-section-title">Was this investigation helpful?</h5>
            {devopsFeedbackSubmitted ? (
              <div className="alert alert-success py-2 px-3 mb-0">Feedback submitted. Thank you!</div>
            ) : (
              <>
                <div className="d-flex align-items-center gap-3 flex-wrap mb-2">
                  <button
                    type="button"
                    className={`kone-btn ${devopsFeedback.rating === 'helpful' ? 'kone-btn-primary' : 'kone-btn-outline'}`}
                    onClick={() => setDevopsFeedback(prev => ({ ...prev, rating: 'helpful' }))}
                  >
                    👍 Helpful
                  </button>
                  <button
                    type="button"
                    className={`kone-btn ${devopsFeedback.rating === 'not_helpful' ? 'kone-btn-danger' : 'kone-btn-secondary'}`}
                    onClick={() => setDevopsFeedback(prev => ({ ...prev, rating: 'not_helpful' }))}
                  >
                    👎 Not Helpful
                  </button>
                </div>
                <textarea
                  className="kone-input mb-2"
                  rows={2}
                  style={{ maxWidth: '500px', resize: 'vertical' }}
                  placeholder="Optional comment..."
                  value={devopsFeedback.comment}
                  onChange={(e) => setDevopsFeedback(prev => ({ ...prev, comment: e.target.value }))}
                />
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="kone-btn kone-btn-primary"
                    onClick={handleSubmitDevopsFeedback}
                    disabled={!devopsFeedback.rating || isSubmittingDevopsFeedback}
                  >
                    {isSubmittingDevopsFeedback ? 'Submitting...' : 'Submit Feedback'}
                  </button>
                  {devopsFeedbackError && (
                    <span className="text-danger"><small>{devopsFeedbackError}</small></span>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  // --- Service Need agent handler (SN path) ---
  const handleServiceNeedAgent = useCallback(async () => {
    if (!incidentDescription || !description) {
      alert('Please provide short description and description before running Service Need agent.');
      return;
    }

    setActiveView('serviceNeedAgent');
    setServiceNeedAgentStatus('invoking');
    setServiceNeedAgentMessage('Starting Service Need agent...');
    setServiceNeedAgentFindings([]);
    setServiceNeedCodeCheckReason('');
    if (serviceNeedPollRef.current) {
      clearInterval(serviceNeedPollRef.current);
      serviceNeedPollRef.current = null;
    }

    try {
      // Service Need always routes through PRM. Fire-and-forget invoke, then
      // poll devops-orchestrator-sessions by the known sessionId.
      const sessionId = `ui-service-need-${crypto.randomUUID()}`;

      console.log('[SN] Calling invokeAgent (fire-and-forget):', { short_description: incidentDescription, description, tribe: 'PRM', payload: null, sessionId });
      graphqlMutation(invokeAgent, {
        input: {
          short_description: incidentDescription,
          description,
          tribe: 'PRM',
          payload: null,
          sessionId,
          // ServiceNow ID from the Incident Resolver page - keys the agent-run
          // incident-table row to match this incident's other records.
          incident_number: incidentNumber || undefined,
        },
      })
        .then((snRes) => {
          const response = snRes?.invokeAgent;
          console.log('[SN] invokeAgent response:', JSON.stringify(response));
          if (response?.status === 'FAILED') {
            console.error('[SN] invokeAgent returned FAILED:', response?.error);
            // Stop polling and show error to user immediately
            if (serviceNeedPollRef.current) {
              clearInterval(serviceNeedPollRef.current);
              serviceNeedPollRef.current = null;
            }
            setServiceNeedAgentStatus('error');
            setServiceNeedAgentMessage(
              response.error || 'Agent invocation failed.'
            );
          }
        })
        .catch((invokeErr) => {
          // Timeout expected for long-running agents; polling will continue.
          console.warn('[SN] invokeAgent error (expected if timeout):', invokeErr.message);
        });
      console.log('[SN] Using sessionId for polling:', sessionId);

      setServiceNeedAgentStatus('polling');
      setServiceNeedAgentMessage('Service Need agent is running...');
      console.log(`[SN] Status -> polling. Polling getSessionTimeline(${sessionId}) every 4s for STATUS...`);

      const start = Date.now();
      let pollCount = 0;
      let pollErrorCount = 0;
      serviceNeedPollRef.current = setInterval(async () => {
        pollCount += 1;
        const elapsedSec = ((Date.now() - start) / 1000).toFixed(1);
        console.log(`[SN] Poll #${pollCount} (${elapsedSec}s) session=${sessionId}`);
        try {
          // Fetch the full timeline in one call (no entryType filter) so we
          // can read STATUS progress, STEP_RESULT(code-review), and DECISION
          // entries without multiple roundtrips.
          const timelineRes = await graphqlQuery(getSessionTimeline, { sessionId });
          pollErrorCount = 0; // reset on success
          const allItems = timelineRes?.getSessionTimeline?.items || [];

          // Partition by entry type
          const statusItems = allItems.filter((e) => e?.entryType === 'STATUS');
          const decisionItems = allItems.filter((e) => e?.entryType === 'DECISION');
          const stepResultItems = allItems.filter((e) => e?.entryType === 'STEP_RESULT');
          const agentMsgItems = allItems.filter((e) => e?.entryType === 'AGENT_MSG');
          const approvalRequestItems = allItems.filter((e) => e?.entryType === 'APPROVAL_REQUEST');
          console.log(`[SN] Poll #${pollCount}: ${statusItems.length} STATUS, ${decisionItems.length} DECISION, ${stepResultItems.length} STEP_RESULT, ${approvalRequestItems.length} APPROVAL_REQUEST entries`);

          // --- HIGHEST PRIORITY: agent is waiting for human approval ---
          // When the agent writes an APPROVAL_REQUEST (or a STATUS whose state is
          // one of AWAITING_APPROVAL_STATUSES) it has paused and is waiting
          // for the user to Allow/Reject. We must navigate to the plan page

          // immediately so the user can decide — otherwise the agent times out
          // server-side and auto-rejects.
          const latestStatusForApproval = statusItems
            .slice()
            .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0];
          const statusStateForApproval = (() => {
            try {
              const p = typeof latestStatusForApproval?.data === 'string'
                ? JSON.parse(latestStatusForApproval.data)
                : latestStatusForApproval?.data;
              return (p?.status || latestStatusForApproval?.status || '').toUpperCase();
            } catch { return ''; }
          })();
          const awaitingApproval =
            approvalRequestItems.length > 0 ||
            AWAITING_APPROVAL_STATUSES.has(statusStateForApproval);

          if (awaitingApproval) {
            clearInterval(serviceNeedPollRef.current);
            serviceNeedPollRef.current = null;
            const totalMs = Date.now() - start;
            console.log(`[SN] ✅ Agent awaiting approval after ${pollCount} polls / ${totalMs}ms — navigating to plan page for user decision`);
            setServiceNeedAgentStatus('done');
            setServiceNeedAgentMessage('Agent is awaiting your approval.');
            navigate(`/devops/sessions/${sessionId}/plan`);
            return;
          }

          // --- Build findings summary from all timeline entries (shown on timeout/error) ---
          const findings = [];
          // Add STATUS milestones in chronological order
          const SN_STATUS_LABELS = {
            QUERYING_GENIE: 'Querying Databricks Genie',
            EXECUTING_QUERY: 'Executing SQL query',
            GENIE_COMPLETED: 'Genie analysis complete',
            DECIDING: 'Decision step reached',
            REVIEWING_CODE: 'Code review in progress',
            COMPLETED: 'Completed',
            SKIPPED_CODE_REVIEW: 'Code review skipped',
            FAILED: 'Failed',
          };
          const sortedStatuses = statusItems.slice().sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
          for (const s of sortedStatuses) {
            const p = (() => { try { return typeof s.data === 'string' ? JSON.parse(s.data) : s.data; } catch { return null; } })();
            const st = (p?.status || s.status || '').toUpperCase();
            const label = SN_STATUS_LABELS[st] || `Status: ${st}`;
            const detail = p?.detail ? String(p.detail).substring(0, 200) : '';
            findings.push({ type: 'status', label, detail, status: st, createdAt: s.createdAt });
          }
          // Add DECISION entries
          for (const d of decisionItems) {
            const p = (() => { try { return typeof d.data === 'string' ? JSON.parse(d.data) : d.data; } catch { return null; } })();
            const invokeCC = p?.invoke_code_checker;
            const reason = p?.reason || '';
            const sn = p?.service_need || '';
            findings.push({
              type: 'decision',
              label: invokeCC ? 'Decision: Invoke code review' : 'Decision: Skip code review',
              detail: `${sn ? `SN: ${sn}` : ''}${reason ? ` — ${reason}` : ''}`,
              createdAt: d.createdAt,
            });
          }
          // Add STEP_RESULT entries
          for (const sr of stepResultItems) {
            const p = (() => { try { return typeof sr.data === 'string' ? JSON.parse(sr.data) : sr.data; } catch { return null; } })();
            const stepId = p?.step_id || 'unknown';
            const stepStatus = p?.status || sr.status || '';
            const output = p?.output ? String(p.output).substring(0, 300) : '';
            findings.push({
              type: 'step_result',
              label: `${stepId}: ${stepStatus}`,
              detail: output,
              createdAt: sr.createdAt,
            });
          }
          // Add AGENT_MSG (Genie reasoning)
          for (const am of agentMsgItems) {
            const p = (() => { try { return typeof am.data === 'string' ? JSON.parse(am.data) : am.data; } catch { return null; } })();
            const msg = p?.message || '';
            const sql = p?.query || '';
            findings.push({
              type: 'agent_msg',
              label: 'Genie Message',
              detail: msg.substring(0, 200) + (sql ? `\nSQL: ${sql.substring(0, 200)}` : ''),
              createdAt: am.createdAt,
            });
          }
          // Sort all findings chronologically and update state
          findings.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
          if (findings.length > 0) {
            setServiceNeedAgentFindings(findings);
            console.log(`[SN] Poll #${pollCount}: ${findings.length} findings accumulated`);
          }

          // --- 1. Check STATUS entries for terminal state ---
          // Agent lifecycle: QUERYING_GENIE → GENIE_COMPLETED → DECIDING → REVIEWING_CODE → terminal
          // Terminal values: COMPLETED | FAILED | SKIPPED_CODE_REVIEW
          const latestStatus = statusItems
            .slice()
            .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0];

          if (latestStatus) {
            const parsed = (() => { try { return typeof latestStatus.data === 'string' ? JSON.parse(latestStatus.data) : latestStatus.data; } catch { return null; } })();
            const state = (parsed?.status || latestStatus.status || '').toUpperCase();
            console.log(`[SN] Poll #${pollCount}: latest STATUS=${state} detail=${parsed?.detail || parsed?.message || '(none)'}`);

            // Show progress message from intermediate statuses
            const SN_PROGRESS_LABELS = {
              QUERYING_GENIE: 'Querying Databricks Genie...',
              EXECUTING_QUERY: 'Executing SQL query...',
              GENIE_COMPLETED: 'Genie analysis complete. Making decision...',
              DECIDING: 'Deciding whether code review is needed...',
              REVIEWING_CODE: 'Reviewing code in GitLab... (this may take several minutes)',
            };
            if (SN_PROGRESS_LABELS[state]) {
              setServiceNeedAgentMessage(SN_PROGRESS_LABELS[state]);
            } else if (parsed?.message) {
              setServiceNeedAgentMessage(parsed.message);
            } else if (parsed?.detail) {
              setServiceNeedAgentMessage(String(parsed.detail).substring(0, 200));
            }

            if (state === 'FAILED' || state === 'REJECTED') {
              clearInterval(serviceNeedPollRef.current);
              serviceNeedPollRef.current = null;
              console.error(`[SN] ❌ Terminal ${state} after ${pollCount} polls`);
              setServiceNeedAgentStatus('error');
              setServiceNeedAgentMessage(
                parsed?.error ||
                parsed?.message ||
                (state === 'REJECTED'
                  ? 'The agent rejected the request (approval may have timed out).'
                  : 'Service Need agent failed.')
              );
              return;
            }

            const SN_TERMINAL_STATES = ['COMPLETED', 'SKIPPED_CODE_REVIEW'];
            if (SN_TERMINAL_STATES.includes(state)) {
              clearInterval(serviceNeedPollRef.current);
              serviceNeedPollRef.current = null;
              const totalMs = Date.now() - start;
              console.log(`[SN] ✅ Terminal ${state} after ${pollCount} polls / ${totalMs}ms`);
              setServiceNeedAgentStatus('done');
              setServiceNeedAgentMessage(parsed?.message || 'Service Need agent completed successfully.');
              console.log('[SN] Navigating to plan page for session:', sessionId);
              navigate(`/devops/sessions/${sessionId}/plan`);
              return;
            }
          }

          // --- 2. Fallback: if a STEP_RESULT with step_id="code-review" exists,
          // the code review completed — the terminal STATUS(COMPLETED) should
          // follow shortly, but navigate now in case it was never written. ---
          const codeReviewResult = stepResultItems.find((e) => {
            try {
              const d = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
              return d?.step_id === 'code-review';
            } catch { return false; }
          });
          if (codeReviewResult) {
            clearInterval(serviceNeedPollRef.current);
            serviceNeedPollRef.current = null;
            const totalMs = Date.now() - start;
            console.log(`[SN] ✅ STEP_RESULT(code-review) found after ${pollCount} polls / ${totalMs}ms — navigating`);
            setServiceNeedAgentStatus('done');
            setServiceNeedAgentMessage('Code review complete.');
            console.log('[SN] Navigating to plan page for session:', sessionId);
            navigate(`/devops/sessions/${sessionId}/plan`);
            return;
          }

          // --- 3. Fallback: DECISION with invoke_code_checker=false means
          // SKIPPED_CODE_REVIEW — navigate if the terminal STATUS was not written. ---
          const skipDecision = decisionItems.find((e) => {
            try {
              const d = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
              return d?.invoke_code_checker === false;
            } catch { return false; }
          });
          if (skipDecision) {
            // Give the backend a few seconds to write the SKIPPED_CODE_REVIEW status
            const decisionAge = Date.now() - new Date(skipDecision.createdAt || 0).getTime();
            if (decisionAge > 15000) { // 15s grace period
              clearInterval(serviceNeedPollRef.current);
              serviceNeedPollRef.current = null;
              const totalMs = Date.now() - start;
              console.log(`[SN] ✅ DECISION(skip code review) found ${(decisionAge/1000).toFixed(0)}s ago, no terminal STATUS — navigating after ${pollCount} polls / ${totalMs}ms`);
              setServiceNeedAgentStatus('done');
              setServiceNeedAgentMessage('Service Need agent completed (code review skipped).');
              console.log('[SN] Navigating to plan page for session:', sessionId);
              navigate(`/devops/sessions/${sessionId}/plan`);
              return;
            }
          }

          if (!latestStatus) {
            console.log(`[SN] Poll #${pollCount}: no entries yet`);
          }

          // Timeout after 15 minutes (agent can take up to ~12 min for Genie + code review)
          if (Date.now() - start > 900000) {
            clearInterval(serviceNeedPollRef.current);
            serviceNeedPollRef.current = null;
            const lastState = sortedStatuses.length > 0 ? ((() => { try { const p = typeof sortedStatuses[sortedStatuses.length-1].data === 'string' ? JSON.parse(sortedStatuses[sortedStatuses.length-1].data) : sortedStatuses[sortedStatuses.length-1].data; return p?.status || sortedStatuses[sortedStatuses.length-1].status || ''; } catch { return ''; } })()) : '';
            // Extract INVOKE_CODE_CHECKER reason from DECISION entries
            const invokeCodeCheckerDecision = decisionItems.find((e) => {
              try {
                const d = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
                return d?.invoke_code_checker === true;
              } catch { return false; }
            });
            const invokeCodeCheckerReason = (() => {
              if (!invokeCodeCheckerDecision) return '';
              try {
                const d = typeof invokeCodeCheckerDecision.data === 'string' ? JSON.parse(invokeCodeCheckerDecision.data) : invokeCodeCheckerDecision.data;
                return d?.reason || '';
              } catch { return ''; }
            })();
            console.error(`[SN] ❌ Polling timed out after ${pollCount} polls / 900000ms. Last state: ${lastState}`);
            setServiceNeedAgentStatus('error');
            let timeoutMsg = lastState
              ? `Agent stuck at "${lastState}" and timed out. See progress below for details.`
              : 'Service Need agent timed out. No progress entries were found.';
            if (invokeCodeCheckerReason) {
              setServiceNeedCodeCheckReason(invokeCodeCheckerReason);
            }
            setServiceNeedAgentMessage(timeoutMsg);
          }
        } catch (err) {
          pollErrorCount += 1;
          console.warn(`[SN] ⚠️ Poll #${pollCount} error (${pollErrorCount}/5):`, err.message || err);
          if (pollErrorCount >= 5) {
            clearInterval(serviceNeedPollRef.current);
            serviceNeedPollRef.current = null;
            console.error(`[SN] ❌ Polling stopped after ${pollErrorCount} consecutive failures`);
            setServiceNeedAgentStatus('error');
            setServiceNeedAgentMessage(err.message || 'Failed to poll Service Need agent status.');
          }
        }
      }, 4000);
    } catch (err) {
      setServiceNeedAgentStatus('error');
      setServiceNeedAgentMessage(err.message || 'Failed to start Service Need agent.');
    }
  }, [incidentDescription, description, navigate, incidentNumber]);

  // --- Request Runbook handler ---
  const handleRequestRunbook = async () => {
    setIsRequestingRunbook(true);
    try {
      await graphqlMutation(requestRunbookMutation, {
        input: {
          incident_number: incidentNumber,
          incident_description: incidentDescription,
          description: description,
        }
      });
      setRequestNotified(true);
      setTimeout(() => window.location.reload(), 2500);
    } catch (err) {
      console.error('Request Runbook API Error:', err);
      alert('Failed to send the runbook request notification. Please try again.');
    } finally {
      setIsRequestingRunbook(false);
    }
  };

  // --- Submit all feedback via a single AppSync updateIncident mutation ---
  const handleSubmitFeedback = async () => {
    if (!incidentDatetime) {
      alert('Incident record not found. Please classify again before submitting feedback.');
      return;
    }

    // Validate: reason is mandatory for thumbs-down
    const missingStepReasons = Object.entries(stepFeedback)
      .filter(([, fb]) => fb.rating === 'down' && !fb.reason?.trim())
      .map(([idx]) => Number(idx) + 1);
    if (missingStepReasons.length > 0) {
      alert(`Please provide a reason for thumbs-down on step(s): ${missingStepReasons.join(', ')}`);
      return;
    }
    if (overallFeedback.rating === 'down' && !overallFeedback.reason?.trim()) {
      alert('Please provide a reason for the overall thumbs-down feedback.');
      return;
    }

    setIsSubmittingFeedback(true);
    try {
      const payload = buildFeedbackPayload();
      await graphqlMutation(updateIncident, payload);
      console.log('[Feedback] Final feedback submitted successfully', payload);
      alert('Feedback submitted successfully. Thank you!');
      window.location.reload();
    } catch (err) {
      console.error('Submit Feedback Error:', err);
      alert('An error occurred while submitting feedback.');
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  return (
    <div className="kone-page-container">
      {toastMessage && (
        <div
          className="toast-container position-fixed top-0 end-0 p-3"
          style={{ zIndex: 1080 }}
        >
          <div className="toast show align-items-center text-bg-dark border-0" role="alert" aria-live="assertive" aria-atomic="true">
            <div className="d-flex">
              <div className="toast-body">{toastMessage}</div>
              <button
                type="button"
                className="btn-close btn-close-white me-2 m-auto"
                aria-label="Close"
                onClick={() => setToastMessage(null)}
              />
            </div>
          </div>
        </div>
      )}
      <h2 className="kone-section-title">ServiceNow Details</h2>
      <div className="kone-form-wrapper incident-form-wrapper">
        <div className="kone-form-card">
          <div className="kone-form-group">
            <label htmlFor="incident_number" className="kone-label">
              ServiceNow ID Number <span className="kone-required">*</span>
            </label>
            <input
              type="text"
              id="incident_number"
              className="kone-input"
              placeholder="Input text"
              value={incidentNumber}
              onChange={(e) => setIncidentNumber(e.target.value)}
              required
            />
          </div>

          <div className="kone-form-group">
            <label htmlFor="incident_description" className="kone-label">
              Short Description <span className="kone-required">*</span>
            </label>
            <input
              type="text"
              id="incident_description"
              className="kone-input"
              placeholder="Input text"
              value={incidentDescription}
              onChange={(e) => setIncidentDescription(e.target.value)}
            />
          </div>

          <div className="kone-form-group">
            <label htmlFor="description" className="kone-label">
              Description <span className="kone-required">*</span>
            </label>
            <textarea
              id="description"
              className="kone-input"
              placeholder="Input text"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{ resize: 'vertical', minHeight: '100px' }}
            />
          </div>

          <div className="kone-form-group">
            <label htmlFor="tribe_information" className="kone-label">
              Tribe <span className="kone-required">*</span>
            </label>
            <select
              id="tribe_information"
              className="kone-select"
              value={tribeInformation}
              onChange={(e) => setTribeInformation(e.target.value)}
              required
            >
              <option value="" disabled>Select tribe</option>
              {config.tribes.map((tribe) => (
                <option key={tribe} value={tribe}>{tribe}</option>
              ))}
            </select>
          </div>

          <div className="kone-btn-row">
            <button
              type="button"
              className="kone-btn kone-btn-primary"
              onClick={handleClassify}
              disabled={isLoading}
            >
              {isLoading ? 'Loading Runbook...' : 'Show Runbook'}
            </button>
          </div>
        </div>
      </div>

      {/* --- Old Classification Result (commented out) ---
      {classificationResult && (
        <div id="classificationResult" className="card p-4">
          <h2>Classification Result</h2>
          <p><strong>Incident Number:</strong> {classificationResult.incident_number}</p>
          <p><strong>Topic Terms:</strong> {classificationResult.topic_terms}</p>
          <p><strong>Predicted Runbook:</strong> {classificationResult.predicted_runbook}</p>
          <p><strong>Runbook Title:</strong> {classificationResult.runbook_title}</p>
          <hr />
          <div id="feedbackSection">
            <p className="mb-2"><strong>Was the classification helpful?</strong></p>
            <div className="d-flex gap-2">
                <button type="button" className="btn btn-success" onClick={() => handleFeedback("Helpful")}>Helpful</button>
                <button type="button" className="btn btn-danger" onClick={() => handleFeedback("Not Helpful")}>Not Helpful</button>
            </div>
          </div>
          <>
              <hr />
              <button
                type="button"
                id="goToResolution"
                className={`btn ${!isProceedEnabled ? 'disabled-button' : 'btn-primary'}`}
                disabled={!isProceedEnabled}
                onClick={handleGoToResolution}
              >
                Proceed to Resolution
              </button>
          </>
        </div>
      )}
      --- */}

      {/* --- Alarm Detected: Show only Investigate --- */}
      {classificationResult && alarmMode && (
        <div className="kone-results-section" style={{ marginTop: '32px', borderTop: '1px solid #E5E7EB', paddingTop: '28px' }}>
          <h2 className="kone-section-title">Alarm Detected</h2>
          <div className="alert alert-info d-flex align-items-center gap-2 mb-3" role="alert">
            <strong>Alarm found in short description:</strong>
            <code style={{ fontSize: '0.9rem', backgroundColor: '#e2e8f0', padding: '2px 8px', borderRadius: '4px' }}>{detectedAlarmName}</code>
          </div>

          <div className="kone-action-bar">
            <button
              type="button"
              className={`kone-btn ${activeView === 'investigate' ? 'kone-btn-primary' : 'kone-btn-outline'}`}
              onClick={() => handleInvestigate()}
              disabled={investigateStatus === 'working'}
            >
              {investigateStatus === 'working' ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Investigating...
                </>
              ) : 'Investigate'}
            </button>
          </div>

          {/* ========== Investigate View (alarm mode) ========== */}
          {renderInvestigateView()}

        </div>
      )}

      {/* --- Service Need (SN): SN info + Investigate --- */}
      {classificationResult && serviceNeedMode && (
        <div className="kone-results-section" style={{ marginTop: '32px', borderTop: '1px solid #E5E7EB', paddingTop: '28px' }}>
          <h2 className="kone-section-title">Service Need Detected</h2>

          <div className="alert alert-info d-flex align-items-center flex-wrap gap-2 mb-3" role="alert">
            <strong>Service Need found:</strong>
            {(classificationResult?.service_needs?.length
              ? classificationResult.service_needs
              : Array.from(new Set(extractServiceNeeds(description)))
            ).map((serviceNeed) => (
              <code
                key={serviceNeed}
                style={{ fontSize: '0.9rem', backgroundColor: '#e2e8f0', padding: '2px 8px', borderRadius: '4px' }}
              >
                {serviceNeed}
              </code>
            ))}
          </div>

          <div className="kone-action-bar mb-3">
            <button
              type="button"
              className="kone-btn kone-btn-primary"
              onClick={handleServiceNeedAgent}
              disabled={
                !canInvokeServiceNeedAgent() ||
                serviceNeedAgentStatus === 'invoking' ||
                serviceNeedAgentStatus === 'polling'
              }
            >
              {!canInvokeServiceNeedAgent()
                ? '🔒 No Access'
                : serviceNeedAgentStatus === 'invoking' || serviceNeedAgentStatus === 'polling' ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Investigating...
                </>
              ) : 'Investigate'}
            </button>
            {!canInvokeServiceNeedAgent() && (
              <div className="alert alert-warning mt-2">
                Your role does not have permission to run this agent. Contact your administrator.
              </div>
            )}
          </div>

          {(serviceNeedAgentStatus === 'invoking' || serviceNeedAgentStatus === 'polling') && (
                <div className="card p-4">
                  <div className="d-flex align-items-center gap-2">
                    <div className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true"></div>
                    <span>{stripUrls(serviceNeedAgentMessage) || 'Service Need agent is running...'}</span>
                  </div>
                </div>
              )}

              {serviceNeedAgentStatus === 'error' && (
                <div>
                  <div className="alert alert-danger" style={{ whiteSpace: 'pre-line' }}>{stripUrls(serviceNeedAgentMessage)}</div>
                  {serviceNeedAgentFindings.length > 0 && (
                    <div className="card p-3 mt-2">
                      <h6 className="mb-2">Agent Progress (before failure/timeout):</h6>
                      <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        {serviceNeedAgentFindings.map((f, i) => (
                          <div key={i} className="d-flex align-items-start gap-2 mb-2" style={{ fontSize: '0.9rem' }}>
                            <span style={{ whiteSpace: 'nowrap' }}>{stripUrls(f.label)}</span>
                            {f.detail && (
                              <span className="text-muted" style={{ wordBreak: 'break-word' }}>
                                {stripUrls(f.detail)}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {serviceNeedCodeCheckReason && (
                    <div className="card p-3 mt-2 border-warning">
                      <h6 className="mb-2">Code Review Reason (INVOKE_CODE_CHECKER)</h6>
                      <p className="mb-0" style={{ whiteSpace: 'pre-line' }}>{stripUrls(serviceNeedCodeCheckReason)}</p>
                    </div>
                  )}
                </div>
              )}

          {serviceNeedAgentStatus === 'done' && (
                <div className="alert alert-success mb-0">
                  {stripUrls(serviceNeedAgentMessage)} Redirecting to results...
                </div>
              )}
        </div>
      )}

      {/* --- Normal Classification Result: Environment Selection --- */}
      {classificationResult && !alarmMode && !serviceNeedMode && (
        <div className="kone-results-section" style={{ marginTop: '32px', borderTop: '1px solid #E5E7EB', paddingTop: '28px' }}>
          <h2 className="kone-section-title">Classification Result</h2>

          {/* Environment Selection Dropdown */}
          <div className="kone-form-group mb-4" style={{ maxWidth: '400px' }}>
            <label htmlFor="environment_type" className="kone-label">
              Is this an AWS or Non-AWS environment?
            </label>
            <select
              id="environment_type"
              className="kone-select"
              value={environmentType || ''}
              onChange={(e) => {
                setEnvironmentType(e.target.value || null);
                setActiveView(null);
              }}
            >
              <option value="" disabled>Select environment</option>
              <option value="AWS">AWS</option>
              <option value="Non-AWS">Non-AWS</option>
            </select>
          </div>

          {/* AWS selected — Investigate button + report view */}
          {environmentType === 'AWS' && (
            <div>
              <div className="kone-action-bar mb-3">
                <button
                  type="button"
                  className={`kone-btn ${activeView === 'investigate' ? 'kone-btn-primary' : 'kone-btn-outline'}`}
                  onClick={() => handleAwsInvestigate()}
                  disabled={investigateStatus === 'working'}
                >
                  {investigateStatus === 'working' ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Investigating...
                    </>
                  ) : 'Investigate'}
                </button>
              </div>

              {/* ========== Investigate View (AWS incident mode) ========== */}
              {renderInvestigateView()}
            </div>
          )}

          {/* Non-AWS selected — show runbook */}
          {environmentType === 'Non-AWS' && (
            <div>
              {dynamodbResults.length > 1 && (
                <div className="alert alert-info d-flex align-items-center gap-2 mb-3" role="alert">
                  <span>📖</span>
                  <strong>A runbook is available for this incident.</strong>
                </div>
              )}

              <div className="kone-action-bar">
                {dynamodbResults.length > 1 && (
                  <>
                    <button
                      type="button"
                      className={`kone-btn ${activeView === 'runbook' ? 'kone-btn-primary' : 'kone-btn-outline'}`}
                      onClick={() => setActiveView('runbook')}
                    >
                      Runbook
                    </button>

                    {hasAutomation && (
                      <button
                        type="button"
                        className={`kone-btn ${activeView === 'aiAgent' ? 'kone-btn-primary' : 'kone-btn-outline'}`}
                        onClick={handleAiAgent}
                      >
                        AI Agent
                      </button>
                    )}
                  </>
                )}

                {dynamodbResults.length <= 1 && (
                  requestNotified ? (
                    <div className="alert alert-success mb-0 py-2 px-3" role="alert">
                      <strong>Team has been notified for Runbook creation.</strong>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="kone-btn kone-btn-outline"
                      onClick={handleRequestRunbook}
                      disabled={isRequestingRunbook}
                    >
                      {isRequestingRunbook ? 'Requesting...' : 'Request Runbook'}
                    </button>
                  )
                )}
              </div>

          {/* ========== Runbook View: Steps Table with Feedback ========== */}
          {activeView === 'runbook' && (
            <div>
              {classificationResult?.confidence != null && classificationResult.confidence < 0.8 && (
                <div className="alert alert-warning d-flex align-items-center gap-2 mb-3" role="alert">
                  <strong>Confidence for this classification is {(classificationResult.confidence * 100).toFixed(1)}% (below 80%). Please properly fill the feedback.</strong>
                </div>
              )}
              <h4 className="kone-section-title mb-3">Runbook Steps</h4>

              {runbookHasScenarioLayout && (
                <div className="mb-3">
                  <p className="mb-2 fw-semibold">Please select your scenario:</p>
                  <div
                    className="mb-3"
                    role="radiogroup"
                    aria-label="Runbook scenario selector"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                      gap: '0.75rem',
                    }}
                  >
                    {runbookScenarioSections.filter((s) => s.scenarioNumber).map((section) => (
                      <button
                        key={section.key}
                        type="button"
                        role="radio"
                        aria-checked={selectedRunbookScenarioKey === section.key}
                        className={`btn btn-sm text-start d-flex align-items-center justify-content-between gap-2 ${selectedRunbookScenarioKey === section.key ? 'btn-primary' : 'btn-outline-secondary'}`}
                        style={{ padding: '0.5rem 0.75rem' }}
                        onClick={() => {
                          setSelectedRunbookScenarioKey(section.key);
                          setShowAllRunbookScenarios(false);
                        }}
                      >
                        <span className="text-truncate">{section.label}</span>
                        <span className="badge bg-light text-dark flex-shrink-0">
                          {section.scenarioNumber ? section.entries.length - 1 : section.entries.length}
                        </span>
                      </button>
                    ))}
                  </div>

                  <div className="form-check form-switch mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="showAllRunbookScenarios"
                      checked={showAllRunbookScenarios}
                      onChange={(e) => setShowAllRunbookScenarios(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="showAllRunbookScenarios">
                      Show all scenarios
                    </label>
                  </div>
                </div>
              )}

              {runbookHasScenarioLayout && showAllRunbookScenarios && (
                <p className="fw-semibold mb-2">Showing all scenarios</p>
              )}

              {visibleRunbookSections.map((section) => {
                // For scenario sections, the first entry is the scenario header
                // row itself — it's now shown as the heading above the table, so
                // drop it from the table body to avoid duplication.
                const displayEntries = section.scenarioNumber
                  ? section.entries.slice(1)
                  : section.entries;
                return (
                <div key={section.key} className="mb-4">
                  {runbookHasScenarioLayout && !showAllRunbookScenarios && (
                    <h5 className="fw-semibold mb-3">{section.label}</h5>
                  )}
                  <div className="table-responsive">
                    <table className="table table-bordered table-hover align-middle" style={{ tableLayout: 'auto', width: '100%' }}>
                      <thead>
                        <tr>
                          <th className="text-center" style={{ whiteSpace: 'nowrap', width: '1%' }}>Step No</th>
                          <th>Description</th>
                          <th style={{ whiteSpace: 'nowrap', width: '1%' }}>Image</th>
                          <th style={{ whiteSpace: 'nowrap', width: '1%' }}>Feedback</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayEntries.map(({ step, index }, entryIdx) => {
                          const fb = stepFeedback[index];
                          const imageUrl = getStepImageUrl(step);
                          const displayStepNumber = runbookHasScenarioLayout
                            ? entryIdx + 1
                            : getStepNumber(step, index);
                          return (
                            <tr key={`${section.key}-${index}`}>
                              <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                                {displayStepNumber}
                              </td>
                              <td className="text-start" style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                                {getStepDescription(step) || JSON.stringify(step)}
                              </td>
                              <td className="text-center align-middle">
                                {imageUrl ? (
                                  <a href={imageUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-secondary">
                                    View Image
                                  </a>
                                ) : (
                                  <span className="text-muted">N/A</span>
                                )}
                              </td>
                              <td>
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <button
                                    type="button"
                                    className={`btn btn-sm ${fb?.userClicked && fb?.rating === 'up' ? 'btn-success' : 'btn-outline-success'}`}
                                    onClick={() => handleStepThumbsUp(index)}
                                    title="Useful"
                                  >
                                    👍
                                  </button>
                                  <button
                                    type="button"
                                    className={`btn btn-sm ${fb?.userClicked && fb?.rating === 'down' ? 'btn-danger' : 'btn-outline-danger'}`}
                                    onClick={() => handleStepThumbsDown(index)}
                                    title="Not useful"
                                  >
                                    👎
                                  </button>
                                  {fb?.rating === 'up' && fb?.userClicked && (
                                    <span className="text-success fw-bold">Useful</span>
                                  )}
                                </div>
                                {fb?.rating === 'down' && (
                                  <textarea
                                    className="kone-input mt-1"
                                    rows={2}
                                    style={{ width: '100%', minWidth: '180px', resize: 'vertical' }}
                                    placeholder="Reason *"
                                    required
                                    value={fb.reason}
                                    onChange={(e) => handleStepReasonChange(index, e.target.value)}
                                    onBlur={(e) => console.log(`[Feedback] Step ${index + 1} reason updated:`, e.target.value)}
                                  />
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
                );
              })}

              {/* Overall Feedback */}
              <div className="card p-3 mt-3 mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                <h5 className="kone-section-title">Overall Feedback</h5>
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <button
                    type="button"
                    className={`kone-btn ${overallFeedback.userClicked && overallFeedback.rating === 'up' ? 'kone-btn-primary' : 'kone-btn-outline'}`}
                    onClick={handleOverallThumbsUp}
                  >
                    👍 Helpful
                  </button>
                  <button
                    type="button"
                    className={`kone-btn ${overallFeedback.userClicked && overallFeedback.rating === 'down' ? 'kone-btn-danger' : 'kone-btn-secondary'}`}
                    onClick={handleOverallThumbsDown}
                  >
                    👎 Not Helpful
                  </button>
                  {overallFeedback.rating === 'down' && (
                    <input
                      type="text"
                      className="kone-input"
                      style={{ maxWidth: '350px' }}
                      placeholder="Reason *"
                      required
                      value={overallFeedback.reason}
                      onChange={(e) => handleOverallReasonChange(e.target.value)}
                      onBlur={(e) => console.log('[Feedback] Overall reason updated:', e.target.value)}
                    />
                  )}
                  {overallFeedback.rating === 'up' && overallFeedback.userClicked && (
                    <span className="text-success fw-bold">Thanks!</span>
                  )}
                </div>
              </div>

              {/* Submit Feedback */}
              <div className="d-flex align-items-center gap-3">
                <button
                  type="button"
                  className="kone-btn kone-btn-primary"
                  onClick={handleSubmitFeedback}
                  disabled={isSubmittingFeedback}
                >
                  {isSubmittingFeedback ? 'Submitting...' : 'Submit Feedback'}
                </button>
              </div>
            </div>
          )}

          {/* ========== AI Agent View ========== */}
          {activeView === 'aiAgent' && (
            <div className="card p-4 text-center">
              {aiAgentStatus === 'working' && (
                <>
                  <div className="spinner-border text-success mb-3" role="status" />
                  <h5 className="text-success">Agent is Working...</h5>
                  {currentAgentStep ? (
                    <div className="alert alert-info mt-3" role="alert" style={{ textAlign: 'left' }}>
                      <strong>🔄 Current Step:</strong>
                      <div className="mt-1">{agentStepLabels[currentAgentStep] || currentAgentStep}</div>
                    </div>
                  ) : (
                    <p className="text-muted">Initializing agent, please wait...</p>
                  )}
                </>
              )}
              {aiAgentStatus === 'done' && (
                <>
                  {/* <div style={{ fontSize: '2.5rem' }}>✅</div> */}
                  <h5 className="mt-2 text-success">{agentStepLabels.final_result}</h5>
                </>
              )}
              {aiAgentStatus === 'error' && (
                <div className="alert alert-danger">
                  Failed to trigger the AI Agent. Please try again or contact support.
                </div>
              )}
            </div>
          )}

            </div>
          )}

        </div>
      )}
    </div>
  );
}

export default IncidentView;
